import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Tag, Save, Info } from 'lucide-react';
import toast from 'react-hot-toast';

export interface PricingSettings {
  minOrderKg: number;
  tier1MaxKg: number;   // anything BELOW this uses tier1Price
  tier1Price: number;   // KSh/kg  (e.g. < 50 kg)
  tier2MaxKg: number;   // anything from tier1MaxKg up to this uses tier2Price
  tier2Price: number;   // KSh/kg  (e.g. 50–100 kg)
  tier3Price: number;   // KSh/kg  (e.g. > tier2MaxKg)
}

export const DEFAULT_PRICING: PricingSettings = {
  minOrderKg: 5,
  tier1MaxKg: 50,
  tier1Price: 80,
  tier2MaxKg: 100,
  tier2Price: 75,
  tier3Price: 70,
};

/** Utility: compute price per kg given total order kg and pricing settings */
export function getPricePerKg(totalKg: number, p: PricingSettings): number {
  if (totalKg < p.tier1MaxKg) return p.tier1Price;
  if (totalKg <= p.tier2MaxKg) return p.tier2Price;
  return p.tier3Price;
}

export async function loadPricingSettings(): Promise<PricingSettings> {
  try {
    const snap = await getDoc(doc(db, 'settings', 'pricing'));
    if (snap.exists()) return { ...DEFAULT_PRICING, ...(snap.data() as PricingSettings) };
  } catch {
    // silently fall back
  }
  return DEFAULT_PRICING;
}

// ─── Admin UI Component ───────────────────────────────────────────────────────
const PriceManager: React.FC = () => {
  const [pricing, setPricing] = useState<PricingSettings>(DEFAULT_PRICING);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isCustom, setIsCustom] = useState(false);

  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(db, 'settings', 'pricing'));
      if (snap.exists()) {
        setPricing({ ...DEFAULT_PRICING, ...(snap.data() as PricingSettings) });
        setIsCustom(true);
      }
      setLoading(false);
    })();
  }, []);

  const set = (field: keyof PricingSettings, value: number) =>
    setPricing(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (pricing.tier1Price <= 0 || pricing.tier2Price <= 0 || pricing.tier3Price <= 0) {
      toast.error('Prices must be greater than 0');
      return;
    }
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'pricing'), pricing);
      setIsCustom(true);
      toast.success('Pricing saved — live on the consumer site now.');
    } catch (e: any) {
      console.error(e);
      toast.error(
        e?.code === 'permission-denied'
          ? 'You need admin privileges to change pricing.'
          : 'Failed to save pricing. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
        Loading price settings…
      </div>
    );
  }

  const examplePrice = (kg: number) =>
    `${kg} kg → KSh ${(kg * getPricePerKg(kg, pricing)).toLocaleString()} (KSh ${getPricePerKg(kg, pricing)}/kg)`;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-2">
          <Tag size={28} className="text-emerald-400" />
          Price Settings
        </h1>
        <p className="text-white/40 mt-1">
          Set tiered per-kg prices for customer orders. Changes take effect immediately on the ordering page.
        </p>
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border bg-white/[0.04] border-white/10">
          <span className={`w-2 h-2 rounded-full ${isCustom ? 'bg-emerald-400' : 'bg-amber-400'}`} />
          <span className="text-white/70">
            {isCustom ? 'Using custom pricing' : 'Using default pricing — save to publish'}
          </span>
        </div>
      </div>

      {/* Minimum order */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
        <h2 className="font-bold text-gray-800 mb-4 text-lg">Minimum Order</h2>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Minimum Order (kg)
            </label>
            <input
              type="number"
              min={1}
              value={pricing.minOrderKg}
              onChange={e => set('minOrderKg', Math.max(1, Number(e.target.value)))}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none text-lg font-bold bg-white text-gray-900"
            />
          </div>
          <div className="text-gray-400 text-sm mt-6">
            Customers cannot order less than this amount.
          </div>
        </div>
      </div>

      {/* Tier pricing */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-5">
        <h2 className="font-bold text-gray-800 text-lg">Tiered Pricing (KSh per kg)</h2>

        {/* Tier 1 */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-green-700 font-bold flex items-center justify-center text-sm">1</div>
          <div className="flex-1 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Below (kg)
              </label>
              <input
                type="number"
                min={1}
                value={pricing.tier1MaxKg}
                onChange={e => set('tier1MaxKg', Math.max(1, Number(e.target.value)))}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none font-semibold bg-white text-gray-900"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Price (KSh/kg)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">KSh</span>
                <input
                  type="number"
                  min={1}
                  value={pricing.tier1Price}
                  onChange={e => set('tier1Price', Math.max(1, Number(e.target.value)))}
                  className="w-full pl-12 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none font-bold bg-white text-gray-900"
                />
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-400 mt-2 w-32">
            Orders under {pricing.tier1MaxKg} kg
          </div>
        </div>

        {/* Tier 2 */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-sm">2</div>
          <div className="flex-1 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Up to (kg)
              </label>
              <input
                type="number"
                min={1}
                value={pricing.tier2MaxKg}
                onChange={e => set('tier2MaxKg', Math.max(1, Number(e.target.value)))}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none font-semibold bg-white text-gray-900"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Price (KSh/kg)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">KSh</span>
                <input
                  type="number"
                  min={1}
                  value={pricing.tier2Price}
                  onChange={e => set('tier2Price', Math.max(1, Number(e.target.value)))}
                  className="w-full pl-12 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none font-bold bg-white text-gray-900"
                />
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-400 mt-2 w-32">
            {pricing.tier1MaxKg}–{pricing.tier2MaxKg} kg
          </div>
        </div>

        {/* Tier 3 */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-100 text-orange-700 font-bold flex items-center justify-center text-sm">3</div>
          <div className="flex-1 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Above (kg)
              </label>
              <div className="px-4 py-2.5 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 text-gray-500 font-semibold">
                {pricing.tier2MaxKg}+ kg
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Price (KSh/kg)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">KSh</span>
                <input
                  type="number"
                  min={1}
                  value={pricing.tier3Price}
                  onChange={e => set('tier3Price', Math.max(1, Number(e.target.value)))}
                  className="w-full pl-12 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none font-bold bg-white text-gray-900"
                />
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-400 mt-2 w-32">
            Over {pricing.tier2MaxKg} kg
          </div>
        </div>
      </div>

      {/* Live preview */}
      <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Info size={16} className="text-orange-500" />
          <h3 className="font-bold text-orange-800 text-sm">Live Preview — Customer Sees</h3>
        </div>
        <div className="space-y-1 text-sm text-orange-700 font-mono">
          <p>• {examplePrice(10)}</p>
          <p>• {examplePrice(50)}</p>
          <p>• {examplePrice(75)}</p>
          <p>• {examplePrice(150)}</p>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full flex items-center justify-center gap-2 py-4 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-colors text-base shadow-sm"
      >
        <Save size={18} />
        {saving ? 'Saving…' : 'Save Pricing Settings'}
      </button>
    </div>
  );
};

export default PriceManager;
